import { api } from "./api";
import { appUrl } from "./customerUrl";

/** Canonical, customer-unique billing URL: /apps/crm/{orgSlug}/settings?tab=billing */
export function billingPath(orgSlug: string | null | undefined): string {
  return `${appUrl("crm", orgSlug, "/settings")}?tab=billing`;
}

export interface PlanPrice {
  app_code: string;
  tier: string;
  display_name?: string;
  amount_cents: number;
  amount: string;
  currency: string;
  interval: string;
  trial_days: number;
  seats: number;
  min_seats?: number;
  max_seats?: number | null;
  billing_unit?: string;
  local_currency?: string;
  local_amount?: string;
  local_amount_cents?: number;
  prices?: Record<string, { currency: string; amount_cents: number; amount: string }>;
  campaign?: CampaignOffer | null;
  monthly?: { currency: string; amount_cents: number; amount: string };
  yearly_list?: { currency: string; amount_cents: number; amount: string };
}

export interface CampaignOffer {
  id: string | null;
  name: string;
  hook: string;
  discount_kind: string;
  discount_value: number;
  interval: string;
  monthly: { currency: string; amount_cents: number; amount: string };
  yearly_list: { currency: string; amount_cents: number; amount: string };
  pay_now: { currency: string; amount_cents: number; amount: string };
  save: { currency: string; amount_cents: number; amount: string };
}

export interface DiscountKickback {
  code: string;
  name: string;
  hook: string;
  discount_kind: string;
  discount_value: number;
  interval: string;
  list: { currency: string; amount_cents: number; amount: string };
  pay_now: { currency: string; amount_cents: number; amount: string };
  save: { currency: string; amount_cents: number; amount: string };
  percent_off: number | null;
  amount_off: number;
  pay_cents: number;
  save_cents: number;
  list_cents: number;
}

export interface NextBill {
  amount_cents: number;
  amount: string;
  currency: string;
  billed_at: string;
  payment_process_at?: string;
  renewal_at?: string;
  payment_lead_days?: number;
  interval: string;
  seat_packs: number;
  pack_seats: number;
  licensed_seats: number;
}

export interface SavedCard {
  brand: string | null;
  last4: string;
}

export interface PlanFeature {
  id: string;
  label: string;
  coming?: string | null;
  starter: boolean;
  professional: boolean;
  enterprise: boolean;
}

export interface OrgBilling {
  org_id: string;
  display_name: string;
  status: string;
  cancelled_at: string | null;
  purge_requested_at: string | null;
  billing_email: string | null;
  cancel_comment?: string | null;
  payfast_configured: boolean;
  active_users?: number;
  payment_method?: SavedCard | null;
  /** A PayFast subscription token is stored: a card is saved with PayFast even when no digits are known. */
  card_on_file?: boolean;
  payfast?: {
    configured: boolean;
    enabled: boolean;
    ready: boolean;
    sandbox: boolean;
  };
  checkout_url?: string | null;
  checkout_process_url?: string | null;
  checkout_fields?: Record<string, string> | null;
  requires_card_capture?: boolean;
  payment_update_url?: string | null;
  notice?: string | null;
  payments_accepted?: boolean;
  payments_accepted_reason?: string | null;
  payment_lead_days?: number;
  checkout_mode?: "local" | "payfast";
  /** True when checkout scheduled the change for the next renewal instead of applying it. */
  scheduled?: boolean;
  /** A paying customer's plan change waiting for the next renewal. */
  pending_plan?: {
    tier: string;
    plan_name: string;
    seats: number;
    interval: "month" | "year";
    amount_cents: number;
    currency: string;
    effective_at: string;
  } | null;
  /** Set after Cancel while the paid term is still running (access continues until access_until). */
  cancellation?: { scheduled: boolean; access_until: string } | null;
  licenses: Array<{
    app_code: string;
    tier: string | null;
    seats: number | null;
    status: string;
    grant_source: string | null;
    access_ends_at: string | null;
  }>;
  events: Array<{ kind: string; app_code: string | null; created_at: string | null }>;
  plan_history?: Array<{
    event_id: string;
    kind: string;
    created_at: string | null;
    to_tier: string | null;
    seats: number | null;
    amount_cents: number | null;
    currency: string | null;
    receipt_available: boolean;
  }>;
  plans: PlanPrice[];
  features?: PlanFeature[];
  next_bill?: NextBill | null;
}

export interface SsoSettings {
  issuer: string;
  client_id: string | null;
  enabled: boolean;
  mock_mode: boolean;
  mock_allowed?: boolean;
  configured: boolean;
  protocol?: "oidc" | "saml";
  domains?: string[];
  metadata_url?: string;
  scopes?: string;
  entity_id?: string;
  certificate_configured?: boolean;
  oidc_redirect_uri?: string;
  saml_acs_url?: string;
  saml_entity_id?: string;
}

export type AiAddonKey = "leadgen" | "loxley" | "mailbox_ai";

export interface AiAddonState {
  addon: AiAddonKey;
  label: string;
  active: boolean;
  switched_on: boolean;
  expires_at: string | null;
}

export interface AiOfferLine {
  addon: AiAddonKey;
  label: string;
  monthly_cents: number;
  quantity: number;
  term_cents: number;
}

export interface AiOffer {
  id: string;
  status: "draft" | "pushed" | "paid" | "cancelled";
  currency: string;
  term_months: number;
  lines: AiOfferLine[];
  subtotal_cents: number;
  tax_percent: number;
  tax_label: string | null;
  tax_cents: number;
  total_cents: number;
  note: string | null;
  created_at: string | null;
  pushed_at: string | null;
  paid_at: string | null;
  payment_method: "saved_card" | "checkout" | "dev_fixture" | null;
  expiries: Partial<Record<AiAddonKey, string>>;
  last_error: string | null;
}

export interface AiBilling {
  addons: AiAddonState[];
  pending: AiOffer | null;
  history: AiOffer[];
  has_saved_card: boolean;
}

export interface AiOfferAcceptResult {
  status: "paid" | "checkout";
  offer: AiOffer;
  process_url?: string;
  fields?: Record<string, string>;
}

export const billingApi = {
  plans: () =>
    api.get<{
      plans: PlanPrice[];
      campaigns?: CampaignOffer[];
      features?: PlanFeature[];
      seat_bands?: Record<string, { min: number; max: number | null; label: string }>;
      turnstile?: { required: boolean; site_key: string | null };
      payments_accepted?: boolean;
      payments_accepted_reason?: string | null;
    }>("/public/plans"),
  signup: (body: {
    org_name: string;
    email: string;
    password?: string;
    display_name?: string;
    tier?: string;
    seats?: number;
    purchase?: boolean;
    campaign_id?: string | null;
    discount_code?: string | null;
    interval?: string;
    terms_accepted?: boolean;
    captcha_token?: string | null;
  }) =>
    api.post<{
      org_id?: string;
      email: string;
      trial_days?: number;
      tier?: string;
      seats?: number;
      purchase: boolean;
      grant_source: string;
      access_days?: number;
      access_ends_at?: string | null;
      emailed?: boolean;
      needs_password?: boolean;
      repeat_trial?: boolean;
    }>("/public/signup", body),
  ssoStart: (email: string) =>
    api.post<{
      mock: boolean;
      protocol?: "mock" | "oidc" | "saml";
      org_name: string;
      nonce?: string;
      authorization_url?: string;
      redirect_url?: string;
    }>("/public/sso/start", { email }),
  ssoCallback: (nonce: string) =>
    api.post<{ status?: string }>("/auth/sso/callback", { nonce }),
  ssoOidcCallback: (code: string, state: string) =>
    api.post<{ status?: string }>("/auth/sso/oidc/callback", { code, state }),
  get: (orgId: string) => api.get<OrgBilling>(`/org/${orgId}/billing`),
  downloadReceipt: (orgId: string, eventId: string) =>
    api.downloadViaLink(`/org/${orgId}/billing/receipts/${eventId}`, `Saleslox-payment-receipt-${eventId}.pdf`),
  previewDiscount: (body: {
    code: string;
    tier: string;
    interval?: string;
    seats?: number;
    amount_cents?: number;
    email?: string | null;
  }) => api.post<DiscountKickback>("/public/discount-codes/preview", body),
  checkout: (
    orgId: string,
    body: {
      app_code: string;
      tier: string;
      seats?: number;
      interval?: string;
      campaign_id?: string | null;
      discount_code?: string | null;
      verification_token: string;
      acknowledged: boolean;
    },
  ) => api.post<OrgBilling>(`/org/${orgId}/billing/checkout`, body),
  previewPlan: (
    orgId: string,
    body: { tier: string; seats?: number; interval?: string; discount_code?: string | null },
  ) =>
    api.post<{
      tier: string;
      plan_name: string;
      seats: number;
      active_users: number;
      interval: string;
      currency: string;
      amount_cents: number;
      amount: string;
      billed_at: string;
      payment_process_at?: string;
      renewal_at?: string;
      payment_lead_days?: number;
      grant_source: string | null;
      scheduled?: boolean;
    }>(`/org/${orgId}/billing/preview`, body),
  startVerification: (orgId: string, purpose: "plan_change" | "card_change") =>
    api.post<{ challenge_id: string; purpose: string; expires_in: number; emailed_to: string }>(
      `/org/${orgId}/billing/verify/start`,
      { purpose },
    ),
  confirmVerification: (orgId: string, body: { purpose: "plan_change" | "card_change"; challenge_id: string; code: string }) =>
    api.post<{ verification_token: string; purpose: string; expires_in: number }>(
      `/org/${orgId}/billing/verify/confirm`,
      body,
    ),
  aiBilling: (orgId: string) => api.get<AiBilling>(`/org/${orgId}/billing/ai`),
  acceptAiOffer: (orgId: string, offerId: string, body: { verification_token: string; acknowledged: boolean }) =>
    api.post<AiBilling & AiOfferAcceptResult>(`/org/${orgId}/billing/ai/offers/${offerId}/accept`, body),
  cancel: (orgId: string, purge: boolean, comment?: string) =>
    api.post<OrgBilling>(`/org/${orgId}/billing/cancel`, { purge, comment }),
  resubscribe: (orgId: string) => api.post<OrgBilling>(`/org/${orgId}/billing/resubscribe`, {}),
  keepCurrentPlan: (orgId: string) => api.post<OrgBilling>(`/org/${orgId}/billing/pending-change/cancel`, {}),
  getSso: (orgId: string) => api.get<SsoSettings>(`/org/${orgId}/billing/sso`),
  saveSso: (
    orgId: string,
    body: {
      issuer: string;
      client_id: string | null;
      enabled: boolean;
      mock_mode: boolean;
      client_secret?: string;
      protocol?: "oidc" | "saml";
      domains?: string[];
      metadata_url?: string;
      scopes?: string;
      entity_id?: string;
      certificate?: string;
    },
  ) => api.put<SsoSettings>(`/org/${orgId}/billing/sso`, body),
};

export const operatorApi = {
  orgs: () => api.get<{ organizations: Array<{ org_id: string; display_name: string; status: string; members: number }>; plans: PlanPrice[] }>("/operator/orgs"),
  org: (orgId: string) => api.get<OrgBilling>(`/operator/orgs/${orgId}`),
  extend: (orgId: string, app_code: string, days: number) =>
    api.post<OrgBilling>(`/operator/orgs/${orgId}/extend`, { app_code, days }),
  purge: (orgId: string) => api.post<{ purged: boolean }>(`/operator/orgs/${orgId}/purge`, {}),
  savePlan: (body: { plans: Array<{ app_code: string; tier: string; amount_cents: number; trial_days: number; seats: number }> }) =>
    api.put<{ plans: PlanPrice[] }>("/operator/plans", body),
};
